import { Attribute, AttributeCode, Measure, MeasureCode, useModelService } from 'services/model/model.service';
import { DataEntry } from 'models/data';
import { CellContext, ColumnDef, Row, SortingFn } from '@tanstack/react-table';
import { MeasureCell } from 'components/table/standardCells/MeasureCell';
import { createArticleImageCellFromUrlAttribute, createSubtitledHeaderCell } from 'components/table/DataTableUtil';
import { useContext } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { TranslatedAttributeLabel } from 'pages/trading/TranslatedAttributeLabel';
import { findIconForCategory } from 'pages/insights-pro/common/icons/CategoryIcons';
import { IntlShape, useIntl } from 'react-intl';
import { Flag } from 'components/Flag';
import { CellValueExporters, formattedRegionCellExporter } from 'components/table/DataTableExportUtil';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { ColumnMeta } from 'components/table/DataTable';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GenericMeasureCellContext = CellContext<any, MeasureEntry>;
export interface ComparisonCell {
  value: number;
  compareValue: number;
  displayValue?: number; // Optional field to override the displayed value without affecting the comparison chip logic
}

export type MeasureEntry = ComparisonCell | number;

export type MeasureEntryRecord = Partial<Record<MeasureCode, MeasureEntry>>;
export type AttributeRecord = Partial<Record<AttributeCode, string>>;
export type DataEntryRowRecord = MeasureEntryRecord & AttributeRecord;

export interface MeasureColumnConfig {
  className?: string | (<T>(row: Row<T>) => string);
  ignoreComparison?: boolean;
  meta?: Partial<ColumnMeta>;
  header?: HeaderOverrides;
  signedValue?: boolean;
}

export interface HeaderOverrides {
  mainHeaderId?: string;
  subHeaderId?: string;
  mainHeaderClass?: string;
  subHeaderClass?: string;
}

export const getMeasureEntryValue = (entry: MeasureEntry) => {
  if (typeof entry === 'number') {
    return entry;
  }
  return entry?.displayValue ?? entry?.value ?? 0;
};

export const useDataEntriesInTables = () => {
  const tenantContext = useContext(TenantConfigContext);
  const intl = useIntl();
  const { getAttributes, getAttribute } = useModelService();

  const dataEntryToDataEntryRowRecord = (entry: DataEntry) => {
    const record: DataEntryRowRecord = {
      ...(entry.attributes as Record<AttributeCode, string>),
    };
    Object.entries(entry.values).forEach(([measureCode, value]) => {
      record[measureCode as MeasureCode] = entry.compareValues
        ? {
            value: value,
            compareValue: entry.compareValues[measureCode] ?? 0,
          }
        : value;
    });

    return record;
  };

  const createAttributeCell = (attribute: Attribute, attributeValue: string) => {
    switch (attribute.code) {
      case 'artImageUrl':
        return createArticleImageCellFromUrlAttribute(attributeValue, tenantContext);
      case 'prdCategory':
        return (
          <div className="flex items-center gap-2">
            {findIconForCategory(attributeValue, 20)}
            <TranslatedAttributeLabel attributeCode={'prdCategory'} attributeValue={attributeValue} />
          </div>
        );

      case 'chnCountry': {
        const countryName = intl.formatDisplayName(attributeValue, { type: 'region' }) ?? attributeValue;
        return (
          <div className="flex items-center gap-2">
            <Flag countryCode={attributeValue} tooltip={countryName} />
            {countryName}
          </div>
        );
      }

      default:
        return <TranslatedAttributeLabel attributeCode={attribute.code} attributeValue={attributeValue} />;
    }
  };

  const createAttributeColumn = (attribute: Attribute) =>
    ({
      id: attribute.code,
      accessorKey: attribute.code,
      enableSorting: false,
      header: attribute.name,
      meta: {
        valueExporter: getExporterForAttribute(attribute),
        skipExport: attribute.code === 'artImageUrl', // Skip exporting the image column
      },
      cell: context => createAttributeCell(attribute, context.getValue() as string),
    }) as ColumnDef<AttributeRecord>;

  const sortComparisonCell: SortingFn<unknown> = (rowA: Row<unknown>, rowB: Row<unknown>, columnId: string) => {
    const a = getMeasureEntryValue(rowA.getValue(columnId));
    const b = getMeasureEntryValue(rowB.getValue(columnId));

    return a === b ? 0 : a > b ? 1 : -1;
  };

  const createExporterForMeasure = (measure: Measure) => {
    return (val: unknown, intl: IntlShape) => {
      const number = getMeasureEntryValue(val as MeasureEntry);
      switch (measure.metric.unit) {
        case MeasureUnitCode.PERCENT:
        case MeasureUnitCode.RATE:
          return intl.formatNumber(number * 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
        case MeasureUnitCode.COUNT:
          return intl.formatNumber(number, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        default:
          return intl.formatNumber(number, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    };
  };

  const emptyStringAwareTranslate = (translationId: string) => {
    if (translationId) {
      return intl.formatMessage({ id: translationId });
    }
    return '';
  };

  const createHeader = (header: HeaderOverrides | undefined, defaultHeader: string) => {
    if (header == null) return defaultHeader;
    // replace a real null mainHeader value with the passed in default, e.g. keep an empty string if passed
    // if an id was passed in it should need still translating while the default is already translated
    const mainHeader = header.mainHeaderId == null ? defaultHeader : emptyStringAwareTranslate(header.mainHeaderId);
    // if both header and sub header exist (even if empty strings) create Subheader column header
    if (header.subHeaderId != null) {
      return createSubtitledHeaderCell(
        mainHeader,
        emptyStringAwareTranslate(header.subHeaderId),
        header.mainHeaderClass ?? '',
        header.subHeaderClass ?? ''
      );
    } else {
      // just a decorated single value header
      return <span className={header.mainHeaderClass}>{emptyStringAwareTranslate(header.mainHeaderId ?? '')}</span>;
    }
  };

  const createHeaderExporter = (config: MeasureColumnConfig, defaultHeaderId: string) => {
    const header = config.header;
    if (!header) return undefined;
    const mainId = header.mainHeaderId ?? defaultHeaderId;

    const mainHeader = emptyStringAwareTranslate(mainId);
    if (header.subHeaderId != null) {
      return (_intl: IntlShape) => `${mainHeader} (${emptyStringAwareTranslate(header.subHeaderId ?? '')})`;
    }
    return (_intl: IntlShape) => header.mainHeaderId;
  };

  const createSortTitles = (config: MeasureColumnConfig, defaultHeaderId: string) => {
    const header = config.header;
    if (!header) return undefined;

    const mainHeader = header.mainHeaderId ?? defaultHeaderId;
    if (header.subHeaderId != null) {
      return { sortTitleId: mainHeader, sortSubtitleId: header.subHeaderId };
    }
    return { sortTitleId: mainHeader };
  };

  const createMeasureColumn = <T extends DataEntryRowRecord>(measure: Measure, config: MeasureColumnConfig = {}) => {
    return {
      accessorKey: measure.code,
      header: createHeader(config.header, measure.metric.name),
      sortingFn: sortComparisonCell,
      meta: {
        valueExporter: createExporterForMeasure(measure),
        headerExporter: createHeaderExporter(config, measure.metric.name),
        ...createSortTitles(config, measure.metric.translationId),
        ...config.meta,
      },
      cell: (context: CellContext<T, MeasureEntry>) => (
        <MeasureCell
          className={
            config.className && typeof config.className === 'function'
              ? config.className(context.row)
              : config.className
          }
          measure={measure}
          context={context}
          signedValue={config.signedValue}
          comparisonActive={!config.ignoreComparison}
        />
      ),
    } as ColumnDef<T>;
  };

  const getAttributeCodeArrayForLeadAttribute = (attributeCode: AttributeCode): AttributeCode[] => {
    switch (attributeCode) {
      case 'prdNumber':
        return ['artImageUrl', 'prdNumber', 'prdName'];
      case 'styBrandColor':
        return ['artImageUrl', 'styBrandColor'];
      case 'artEan':
        return ['artImageUrl', 'styBrandColor', 'artSize', 'artEan'];
      default:
        return [attributeCode];
    }
  };

  const getAttributeArrayForLeadAttribute = (attributeCode: AttributeCode) => {
    return getAttributeCodeArrayForLeadAttribute(attributeCode).map(code => getAttribute(code));
  };

  const getAttributeColumnSetForAttribute = (attributeCode: AttributeCode): ColumnDef<AttributeRecord>[] => {
    const [prdName, prdNumber, artImageUrl, styBrandColor, artSize, artEan] = getAttributes([
      'prdName',
      'prdNumber',
      'artImageUrl',
      'styBrandColor',
      'artSize',
      'artEan',
    ]);

    const imageColumn = createAttributeColumn(artImageUrl);
    const prdNameColumn = createAttributeColumn(prdName);

    if (attributeCode === 'prdNumber') {
      const codeColumn = createAttributeColumn(prdNumber);
      return [imageColumn, codeColumn, prdNameColumn];
    }

    const styleColumn = createAttributeColumn(styBrandColor);
    const sizeColumn: ColumnDef<AttributeRecord> = createAttributeColumn(artSize);

    if (attributeCode === 'styBrandColor') {
      return [imageColumn, styleColumn];
    }

    if (attributeCode === 'artEan') {
      const articleColumn = createAttributeColumn(artEan);
      return [imageColumn, styleColumn, sizeColumn, articleColumn];
    }

    return [createAttributeColumn(getAttribute(attributeCode))];
  };

  const createExportersForMeasures = (measures: Measure[]) => {
    const exporters: CellValueExporters = {};
    measures.forEach(measure => {
      exporters[measure.code] = createExporterForMeasure(measure);
    });
    return exporters;
  };

  const getExporterForAttribute = (attribute: Attribute) => {
    if (attribute.code === 'chnCountry') {
      return formattedRegionCellExporter;
    }
    return undefined;
  };

  const createExportersForAttributes = (attributes: Attribute[]) => {
    const exporters: CellValueExporters = {};
    attributes.forEach(attribute => {
      const exporter = getExporterForAttribute(attribute);
      if (exporter) {
        exporters[attribute.code] = exporter;
      }
    });
    return exporters;
  };

  return {
    dataEntryToDataEntryRowRecord,
    createAttributeCell,
    createMeasureColumn,
    createAttributeColumn,
    getAttributeArrayForLeadAttribute,
    getAttributeColumnSetForAttribute,
    createExporterForMeasure,
    createExportersForMeasures,
    createExportersForAttributes,
  };
};
